import { type ReactElement, type ReactNode, useCallback } from 'react'
import { cn } from '@util'
import { isNotEmpty } from '@terros/common'
import { Size, SizePrimitives } from '@front/model'
import { MaterialSymbolIcon } from '../icon/MaterialSymbolIcon'
import { type TDataTableColumn } from './types'
import styles from './table.module.css'

export const ROW_HEIGHT = 36
const DIVIDER_HEIGHT = 1
export const HEADER_FALLBACK_HEIGHT = ROW_HEIGHT + DIVIDER_HEIGHT

type Props<T extends Record<string, unknown> | unknown[]> = {
  record: T
  visibleColumns: TDataTableColumn<T>[]
  onClick?: VoidFunction
  rowIndex: number
  isStriped?: boolean
  nestedTable?: boolean | number
  isPinned?: boolean
  isExpandable?: boolean
  isExpanded?: boolean
  showExpansionChevron?: boolean
  pinnedTopOffset?: number
  pinLastColumn?: boolean
}

export const Row = <T extends Record<string, unknown> | unknown[]>({
  onClick,
  record,
  visibleColumns,
  rowIndex,
  nestedTable,
  isStriped,
  isPinned,
  isExpandable,
  isExpanded,
  showExpansionChevron,
  pinnedTopOffset = HEADER_FALLBACK_HEIGHT,
  pinLastColumn,
}: Props<T>): ReactElement => {
  const getCellStyle = useCallback(
    (column: TDataTableColumn<T>, isFirstColumn: boolean): React.CSSProperties => {
      const fixedWidth = column.width && !column.maxWidth && !column.grow ? `${column.width}px` : 'auto'
      return {
        width: fixedWidth,
        maxWidth: fixedWidth,
        paddingLeft:
          isFirstColumn && nestedTable ? Size.space['3xl'] + toNestedLevel(nestedTable) * Size.space['2xl'] : undefined,
        cursor: onClick ? 'pointer' : undefined,
      }
    },
    [nestedTable, onClick]
  )

  const getCellStyleWithPinning = useCallback(
    (column: TDataTableColumn<T>, isFirstColumn: boolean, isLastColumn: boolean): React.CSSProperties => {
      const baseStyle = getCellStyle(column, isFirstColumn)

      if (!isPinned) return baseStyle

      const isPinnedSideColumn = isFirstColumn || (pinLastColumn === true && isLastColumn)
      return {
        ...baseStyle,
        position: 'sticky',
        top: `${pinnedTopOffset + ROW_HEIGHT * rowIndex}px`,
        zIndex: isPinnedSideColumn ? 20 : 1,
      }
    },
    [getCellStyle, isPinned, pinLastColumn, pinnedTopOffset, rowIndex]
  )

  return (
    <div
      className={styles.row}
      data-expanded={isExpanded}
      data-striped={isStriped}
      aria-expanded={isExpandable ? isExpanded : undefined}
      onClick={onClick}
    >
      {visibleColumns.map((column, colIndex) => {
        const children = column.render(record)
        const shouldRenderChevron = showExpansionChevron && colIndex === 0
        const isLastColumn = colIndex === visibleColumns.length - 1
        const cellClassName = column.cellClassName?.(record)
        return (
          <div
            className={cn(styles.cell, cellClassName)}
            data-align={column.textAlign ?? getDefaultTextAlign(children)}
            data-custom-bg={isNotEmpty(cellClassName) || undefined}
            data-is-first-column={colIndex === 0}
            data-nested={nestedTable ? true : undefined}
            data-no-wrap={column.noWrap}
            key={`cell-${rowIndex}-${column.accessor}`}
            style={getCellStyleWithPinning(column, colIndex === 0, isLastColumn)}
          >
            {shouldRenderChevron && (
              <span
                className={styles.expansionChevron}
                data-expanded={isExpanded}
                data-visible={isExpandable}
                aria-hidden
              >
                <MaterialSymbolIcon icon='chevron_right' size={SizePrimitives[4]} color='var(--mantine-color-dimmed)' />
              </span>
            )}
            {children}
          </div>
        )
      })}
    </div>
  )
}

const getDefaultTextAlign = (cellValue: ReactNode): 'left' | 'center' | 'right' => {
  if (typeof cellValue === 'number') return 'right'
  return 'left'
}

const toNestedLevel = (nestedTable: boolean | number): number => {
  if (typeof nestedTable === 'number') return nestedTable
  if (nestedTable) return 1
  return 0
}
